/* eslint-disable react-hooks/exhaustive-deps */
import {
  CheckOutlined,
  CloseOutlined,
  DeleteTwoTone,
  PlusOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import {
  CREATE_DRIVER,
  DELETE_DRIVER,
  Driver,
  LIST_DRIVERS,
} from '@frontend/data-access';
import { App, Button, Input, Table } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import useTranslation from 'next-translate/useTranslation';
import { useMemo, useState } from 'react';
import { TabHeader } from '../components';

const SubmissionForm = () => {
  const { notification } = App.useApp();
  const { t } = useTranslation('common');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { data: driversData, loading: loadingDrivers } = useQuery<{
    listDrivers: Driver[];
  }>(LIST_DRIVERS);

  const [createDriver, { loading: createLoading }] = useMutation(
    CREATE_DRIVER,
    {
      refetchQueries: [{ query: LIST_DRIVERS }],
      onError: (error) => {
        notification.error({
          message: t('error'),
          description: error.message,
        });
      },
    }
  );

  const [deleteDriver, { loading: deleteLoading }] = useMutation(
    DELETE_DRIVER,
    {
      refetchQueries: [{ query: LIST_DRIVERS }],
      onError: (error) => {
        notification.error({
          message: t('error'),
          description: error.message,
        });
      },
    }
  );

  const handleAddRow = () => {
    setEditingId('new');
  };

  const handleDeleteRow = (id: string) => {
    setDeletingId(id);
    deleteDriver({
      variables: { id },
    });
  };

  const handleSave = async (id: string, name: string) => {
    if (name.trim()) {
      if (id === 'new') {
        await createDriver({
          variables: { driver: name.trim() },
        });
      }
      setEditingId(null);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
  };

  const columns: ColumnsType<Driver> = useMemo(
    () => [
      {
        title: t('no'),
        width: 70,
        render: (_, __, index) => index + 1,
      },
      {
        title: t('name'),
        dataIndex: 'driver',
        render: (name, record) => {
          if (editingId === record.id || (editingId === 'new' && !record.id)) {
            return (
              <Input
                placeholder={t('enter_name')}
                defaultValue={name}
                autoFocus
                onPressEnter={(e) =>
                  handleSave(
                    record.id || 'new',
                    (e.target as HTMLInputElement).value
                  )
                }
              />
            );
          }
          return <span>{name}</span>;
        },
      },
      {
        title: t('action'),
        key: 'action',
        width: 120,
        render: (_, record) => {
          if (editingId === record.id || (editingId === 'new' && !record.id)) {
            return (
              <span className="flex gap-2">
                <Button
                  type="text"
                  icon={<CheckOutlined className="text-green-600" />}
                  onClick={(e) => {
                    const input = (e.target as HTMLElement)
                      .closest('tr')
                      ?.querySelector('input') as HTMLInputElement;
                    if (input) {
                      handleSave(record.id || 'new', input.value);
                    }
                  }}
                  loading={createLoading}
                />
                <Button
                  type="text"
                  icon={<CloseOutlined className="text-red-600" />}
                  onClick={handleCancel}
                />
              </span>
            );
          }
          return (
            <Button
              type="text"
              icon={<DeleteTwoTone twoToneColor="#FF4D4F" />}
              onClick={() => handleDeleteRow(record.id)}
              loading={deleteLoading && deletingId === record.id}
            />
          );
        },
      },
    ],
    [editingId, createLoading, deleteLoading, deletingId]
  );

  const drivers = useMemo(() => {
    const existingDrivers = driversData?.listDrivers || [];
    return editingId === 'new'
      ? [...existingDrivers, { id: 'new', driver: '' }]
      : existingDrivers;
  }, [driversData?.listDrivers, editingId]);

  return (
    <div className="space-y-4">
      <TabHeader
        title={t('strategic_driver_title')}
        description={t('strategic_driver_description')}
      />

      <div className="space-y-4">
        <Table
          bordered
          size="small"
          columns={columns}
          dataSource={drivers}
          pagination={false}
          rowKey="id"
          loading={loadingDrivers}
        />

        <Button
          type="default"
          icon={<PlusOutlined />}
          onClick={handleAddRow}
          shape="round"
          disabled={editingId !== null}
        >
          {t('add_row')}
        </Button>
      </div>
    </div>
  );
};

export default SubmissionForm;
